import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import {
  AiGenerationErrorLogDto,
  AiGenerationErrorType,
  GfFlyerItemList,
} from "@swagger/typescript-fetch-goflyer";
import { formatDateInEasternTime } from "../../../utils/dateUtil/formatDateInEasternTime";

const DATE_TIME_FORMAT = "MMM D, YYYY h:mm A";
const EMPTY_VALUE = "—";

/**
 * The details of the latest Smart Flyer AI generation run, shown alongside the
 * error log so a failure can be diagnosed from a single place.
 */
export type AiGenerationRunDetails = Partial<
  Pick<
    GfFlyerItemList,
    | "aiGenerationModel"
    | "aiGenerationEffort"
    | "aiGenerationErrorType"
    | "aiGenerationFailedAt"
    | "aiGenerationAutoRetryCount"
    | "aiGenerationLastAutoRetryAt"
  >
>;

const errorTypeLabels: Record<string, string> = {
  [AiGenerationErrorType.USAGELIMIT]: "Usage Limit",
  [AiGenerationErrorType.WEEKLYLIMIT]: "Weekly Limit",
  [AiGenerationErrorType.AUTH]: "Auth",
  [AiGenerationErrorType.OVERLOADED]: "Overloaded",
  [AiGenerationErrorType.TIMEOUT]: "Timeout",
  [AiGenerationErrorType.PARSE]: "Parse",
  [AiGenerationErrorType.SPAWN]: "Spawn",
  [AiGenerationErrorType.UNKNOWN]: "Unknown",
};

const errorTypeColors: Record<
  string,
  "default" | "info" | "success" | "warning" | "error"
> = {
  [AiGenerationErrorType.USAGELIMIT]: "warning",
  [AiGenerationErrorType.WEEKLYLIMIT]: "warning",
  [AiGenerationErrorType.OVERLOADED]: "warning",
  [AiGenerationErrorType.TIMEOUT]: "warning",
  [AiGenerationErrorType.AUTH]: "error",
  [AiGenerationErrorType.PARSE]: "error",
  [AiGenerationErrorType.SPAWN]: "error",
  [AiGenerationErrorType.UNKNOWN]: "default",
};

/**
 * Whether a flyer item list carries any AI run details worth opening the modal
 * for, even when no structured error log was recorded.
 */
export const hasAiGenerationRunDetails = (
  runDetails?: AiGenerationRunDetails | null,
): boolean =>
  Boolean(
    runDetails &&
      (runDetails.aiGenerationModel ||
        runDetails.aiGenerationEffort ||
        runDetails.aiGenerationErrorType ||
        runDetails.aiGenerationFailedAt ||
        runDetails.aiGenerationLastAutoRetryAt ||
        (runDetails.aiGenerationAutoRetryCount ?? 0) > 0),
  );

const formatDateTime = (value?: Date | string | null) =>
  value ? formatDateInEasternTime(value, DATE_TIME_FORMAT) : EMPTY_VALUE;

interface RunDetailProps {
  label: string;
  children: React.ReactNode;
}

const RunDetail: React.FC<RunDetailProps> = ({ label, children }) => (
  <Box sx={{ minWidth: "160px" }}>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", fontWeight: 600, display: "block" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ mt: 0.25 }}>
      {children}
    </Typography>
  </Box>
);

interface AiErrorLogModalProps {
  open: boolean;
  onClose: () => void;
  errorLog: AiGenerationErrorLogDto | null;
  runDetails?: AiGenerationRunDetails | null;
}

/**
 * Modal component to display AI Generation Error Log details
 * Shows the run details (model, effort, failure classification and automatic
 * retry bookkeeping), total images, successful/failed counts, and individual
 * error details
 */
export const AiErrorLogModal: React.FC<AiErrorLogModalProps> = ({
  open,
  onClose,
  errorLog,
  runDetails,
}) => {
  const { t } = useTranslation();

  const showRunDetails = hasAiGenerationRunDetails(runDetails);

  if (!errorLog && !showRunDetails) return null;

  const successRate =
    errorLog && errorLog.totalImages > 0
      ? ((errorLog.successfulImages / errorLog.totalImages) * 100).toFixed(1)
      : "0";

  const errorType = runDetails?.aiGenerationErrorType;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="ai-error-log-dialog-title"
    >
      <DialogTitle id="ai-error-log-dialog-title">
        {"AI Generation Error Log"}
      </DialogTitle>
      <DialogContent dividers>
        {showRunDetails && (
          <Box sx={{ mb: 3 }} data-testid="ai-error-log-run-details">
            <Typography variant="h6" gutterBottom>
              {"Run Details"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <RunDetail label="Model">
                {runDetails?.aiGenerationModel ?? EMPTY_VALUE}
              </RunDetail>
              <RunDetail label="Effort">
                {runDetails?.aiGenerationEffort ?? EMPTY_VALUE}
              </RunDetail>
              <RunDetail label="Error Type">
                {errorType ? (
                  <Chip
                    label={errorTypeLabels[errorType] ?? errorType}
                    color={errorTypeColors[errorType] ?? "default"}
                    size="small"
                  />
                ) : (
                  EMPTY_VALUE
                )}
              </RunDetail>
              <RunDetail label="Failed At">
                {formatDateTime(runDetails?.aiGenerationFailedAt)}
              </RunDetail>
              <RunDetail label="Auto Retries">
                {runDetails?.aiGenerationAutoRetryCount ?? 0}
              </RunDetail>
              <RunDetail label="Last Auto Retry">
                {formatDateTime(runDetails?.aiGenerationLastAutoRetryAt)}
              </RunDetail>
            </Box>
          </Box>
        )}

        {errorLog && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {"Summary"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Chip
                label={`Total Images: ${errorLog.totalImages}`}
                color="default"
                variant="outlined"
              />
              <Chip
                label={`Successful: ${errorLog.successfulImages}`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`Failed: ${errorLog.failedImages}`}
                color="error"
                variant="outlined"
              />
              <Chip
                label={`Success Rate: ${successRate}%`}
                color={parseFloat(successRate) >= 80 ? "success" : "warning"}
                variant="filled"
              />
            </Box>
          </Box>
        )}

        {errorLog?.generalError && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {t(messages.GeneralError())}
              </Typography>
              <Typography variant="body2">{errorLog.generalError}</Typography>
            </Alert>
          </Box>
        )}

        {errorLog?.errors && errorLog.errors.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {`Image Errors (${errorLog.errors.length})`}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {"Image Index"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {"Flyer Index"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {"Image ID"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>{"Error"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorLog.errors.map((error, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <TableCell>{error.imageIndex}</TableCell>
                      <TableCell>
                        {error.flyerIndex !== undefined
                          ? error.flyerIndex
                          : "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={error.imageId}
                      >
                        {error.imageId}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: "300px",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {error.error}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {errorLog.errors.length === 50 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Error log is limited to 50 entries. There may be additional
                errors not shown.
              </Alert>
            )}
          </Box>
        )}

        {(!errorLog?.errors || errorLog.errors.length === 0) &&
          !errorLog?.generalError && (
            <Alert severity="info">No error details available.</Alert>
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          {t(messages.Close())}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
